import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from 'constants/apiConfig';
import {
  selectGroupGuid,
  useAppSelector,
} from '../../../slices/commonSelectors';
import type { ActionReminderPolicy } from '../actionsTable/reminderPolicyModel';
import { actionErrorMessage } from './actionErrors';

export const reminderPolicyKeys = {
  byGroup: (groupGuid: string) =>
    ['action_reminder_policy', groupGuid] as const,
};

export const useActionReminderPolicy = (enabled = true) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  return useQuery({
    queryKey: reminderPolicyKeys.byGroup(groupGuid),
    queryFn: async () => {
      const resp = await axios.get<ActionReminderPolicy>(
        `${config.apiGateway.ics_action_reminder_policy}/${groupGuid}`
      );
      return resp.data;
    },
    enabled: enabled && !!groupGuid,
  });
};

export const useUpdateActionReminderPolicy = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<ActionReminderPolicy, 'groupGuid'>) => {
      const resp = await axios.put<ActionReminderPolicy>(
        `${config.apiGateway.ics_action_reminder_policy}/${groupGuid}`,
        body
      );
      return resp.data;
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(reminderPolicyKeys.byGroup(groupGuid), policy);
      toast.success(
        policy.enabled
          ? 'Reminder policy saved. Open tasks will be scheduled.'
          : 'Reminder policy saved. Reminders are off.'
      );
    },
    onError: (error) => {
      toast.error(actionErrorMessage(error));
    },
  });
};
